import { memo } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
// Import Store
import { useAppSelector } from 'store';
import { selectPageValidation } from 'store/slices/pageValidation';
import { selectPartnerSettings } from 'store/slices/partnerSettings';
// Import Hooks
import { useValidator } from 'hooks';
// Import UI components
import { SegmentItemsConfig } from './config';
// Import Components
import SegmentItem from './SegmentItem';

const { enableDropHere } = SegmentItemsConfig;

const SegmentItems = ({ getRowByIndex, dropAcceptType, firstSegment, setFirstSegment, segments, setSegments }) => {
    const validator = useValidator();
    const pageValidation = useAppSelector(selectPageValidation);
    const { CompositeSegmentMaxCount } = useAppSelector(selectPartnerSettings) ?? {};

    const mapSegmentItem = ({ data }) => {
        return {
            title: data.Name,
            type: data.Type,
            segmentId: data.SegmentId,
            enableDropHere: false,
            data,
        };
    };

    const pushDropHereIfNecessary = (tmpSegment) => {
        if (!isNil(CompositeSegmentMaxCount) && tmpSegment.length !== CompositeSegmentMaxCount - 1) {
            const push = tmpSegment.reduce((acc, value) => (acc = acc && !value.enableDropHere), true);
            if (push) {
                tmpSegment.push({ ...enableDropHere });
            }
        }
    };

    const clearHandler = (isFirst: boolean, index?: number) => {
        return () => {
            if (isFirst) {
                setFirstSegment({ ...enableDropHere });
                validator({ ChoosedSegmentsFirstChild: false, ChoosedSegments: false });
            } else {
                setSegmentsValue(index, false);
            }
        };
    };

    const dropHandler = (isFirst: boolean, index?: number) => {
        return (rowIndex) => {
            if (isFirst) {
                setFirstSegment(mapSegmentItem(getRowByIndex(rowIndex)));
                validator({ ChoosedSegmentsFirstChild: true, ChoosedSegments: !segments[0].enableDropHere });
            } else {
                setSegmentsValue(index, true, rowIndex);
            }
        };
    };

    const setSegmentsValue = (index: Nilable<number> = undefined, isDrop: boolean, rowIndex?: number) => {
        const tmpSegments = [...segments];
        if (isDrop) {
            tmpSegments[index ?? ''] = mapSegmentItem(getRowByIndex(rowIndex));
        } else {
            tmpSegments.splice(index ?? 0, 1);
        }
        pushDropHereIfNecessary(tmpSegments);
        setSegments(tmpSegments);
        validator({ ChoosedSegments: !tmpSegments[0]?.enableDropHere });
    };

    return (
        <div>
            <div className="dropping-container-info-inner">
                <SegmentItem
                    dropSegment={dropHandler(true)}
                    itemData={firstSegment}
                    acceptType={dropAcceptType}
                    onClear={clearHandler(true)}
                    isValid={
                        isNil(pageValidation.ChoosedSegmentsFirstChild?.isValid) ||
                        pageValidation.ChoosedSegmentsFirstChild?.isValid
                    }
                />
            </div>
            {segments.map((item, index) => (
                <div className="dropping-container-info-inner" key={index}>
                    <SegmentItem
                        key={index}
                        dropSegment={dropHandler(false, index)}
                        itemData={item}
                        acceptType={dropAcceptType}
                        onClear={clearHandler(false, index)}
                        isValid={
                            isNil(item?.isValid)
                                ? index !== 0 ||
                                  isNil(pageValidation.ChoosedSegments?.isValid) ||
                                  pageValidation.ChoosedSegments.isValid
                                : item.isValid
                        }
                    />
                </div>
            ))}
        </div>
    );
};

SegmentItems.propTypes = {
    getRowByIndex: PropTypes.func.isRequired,
    firstSegment: PropTypes.object.isRequired,
    segments: PropTypes.array.isRequired,
    setFirstSegment: PropTypes.func.isRequired,
    setSegments: PropTypes.func.isRequired,
    dropAcceptType: PropTypes.string.isRequired,
};

export default memo(SegmentItems);
